import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, 
  Phone, 
  FileText,
  MapPin,
  Globe,
  Zap,
  Link as LinkIcon,
  BookOpen,
  AlertCircle,
  Search,
  Code,
  Calendar,
  ArrowRight
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LawnCareWebsiteSEO = () => {
  const faqs = [
    {
      question: "How long does it take to rank on Google for lawn care?",
      answer: "Typically 3–6 months for less competitive keywords and city combinations. Highly competitive metros may take 6–12+ months. Consistency matters more than speed."
    },
    {
      question: "Do I need a separate page for each city I serve?",
      answer: "For your primary service area, yes—individual city pages help you rank for '[service] in [city]' searches. For secondary areas, you can group nearby cities on regional pages."
    },
    {
      question: "How many service pages should I have?",
      answer: "One page per distinct service. If you offer mowing, fertilization, aeration, cleanups, and landscaping, that's 5 service pages minimum. Don't combine unrelated services."
    },
    {
      question: "Does my website affect my Google Maps ranking?",
      answer: "Yes. Your website supports your Google Business Profile through NAP consistency, service alignment, and content relevance. Google uses your website to verify information about your business."
    },
    {
      question: "Should I put pricing on my website?",
      answer: "Starting-at pricing or price ranges are fine and can help qualify leads. You don't need exact quotes—many businesses use 'starting at $X' or 'request a quote' effectively."
    },
    {
      question: "How important is mobile speed?",
      answer: "Critical. Most lawn care searches happen on phones. If your site is slow or hard to use on mobile, you'll lose leads and rank lower. Aim for under 3-second load times."
    },
    {
      question: "What's the difference between SEO and Google Ads?",
      answer: "SEO = organic rankings (free clicks, takes time). Google Ads = paid ads (instant visibility, pay per click). Both can work, but SEO builds long-term traffic at lower ongoing cost."
    },
    {
      question: "Do I need to blog to rank?",
      answer: "Not necessarily. Service pages and city pages typically matter more for local SEO. A blog can help, but it's lower priority than getting your core pages right first."
    }
  ];

  const commonMistakes = [
    { mistake: "No city mentioned on the homepage", fix: "Add your primary city to your H1, title tag, and meta description." },
    { mistake: "One page listing all services", fix: "Create a dedicated page for each service with 400+ words." },
    { mistake: "Duplicate city pages with only city name swapped", fix: "Add unique content about each city: projects done, neighborhoods, local details." },
    { mistake: "No internal links between pages", fix: "Link service pages to city pages, city pages to contact, etc." },
    { mistake: "Only stock photos, no real work", fix: "Add before/after photos of your actual jobs." },
    { mistake: "Slow-loading pages (5+ seconds)", fix: "Compress images to WebP, enable lazy loading, reduce plugins." },
    { mistake: "Phone number differs from GBP listing", fix: "Use the same phone number everywhere: website, GBP, social, citations." },
    { mistake: "No CTA visible without scrolling", fix: "Put your phone number and quote button in the hero section." }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-seo/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-seo/#webpage",
    "name": "Lawn Care Website SEO Guide | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/lawn-care-website-seo/",
    "description": "Local SEO checklist for lawn care websites: titles, service pages, city pages, internal linking, schema, speed, and GBP alignment—step by step.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  const lastUpdated = "December 2024";

  return (
    <>
      <SEO 
        title="Lawn Care Website SEO Guide"
        description="Local SEO checklist for lawn care websites: titles, service pages, city pages, internal linking, schema, speed, and GBP alignment—step by step."
        keywords="lawn care website seo, local seo for lawn care, lawn care seo checklist, lawn mowing seo, lawn care service pages seo"
        canonical="https://www.leafrushmarketing.com/lawn-care-website-seo/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-seo/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Local SEO Guide
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Lawn Care Website SEO: Local Ranking Checklist
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                What to do on your website so you rank in your service area—step by step, no fluff.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/websites">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    See Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Answer Checklist */}
        <section className="pb-12">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    Quick Answer: Top 10 Local SEO Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    {[
                      "Title tags include service + city (e.g., 'Lawn Mowing in Austin, TX')",
                      "One dedicated page per service you offer",
                      "City/area pages for each primary service area",
                      "Phone number matches your Google Business Profile exactly",
                      "Internal links connect service pages → city pages → contact",
                      "Images compressed (WebP, <150kb), lazy loaded",
                      "Mobile loads in under 3 seconds",
                      "CTA (call/quote) visible above the fold",
                      "NAP (Name, Address, Phone) consistent everywhere",
                      "Schema markup on key pages (Organization, Service, FAQPage)"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How Local SEO Works */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Search className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  How Local SEO Actually Works
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Google ranks local businesses based on three factors. Your website influences all of them.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Relevance</h3>
                    <p className="text-sm text-muted-foreground">
                      Does your site clearly say what you do and where? Title tags, service pages, and content tell Google you're relevant to "lawn care in [city]."
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Proximity</h3>
                    <p className="text-sm text-muted-foreground">
                      How close is your business to the searcher? City pages and address info help Google understand your service area.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Globe className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Prominence</h3>
                    <p className="text-sm text-muted-foreground">
                      How well-known/trusted is your business? Reviews, backlinks, and consistent NAP info across the web build prominence.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 p-4 bg-background border rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  <strong className="text-foreground">Key insight:</strong> Your website SEO supports both Google Maps (the "3-pack") and organic search results below it. A well-optimized site helps you show up in both places.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* On-Page SEO Basics */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-4">
                On-Page SEO Basics
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                The fundamentals that every lawn care website needs to rank locally.
              </p>

              {/* Title Tags + H1s */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                      Title Tags + H1s
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Your title tag (what shows in Google results) and H1 (main headline on page) should include your service and city.
                    </p>
                    
                    <div className="bg-accent/30 rounded-lg p-4 space-y-3">
                      <div>
                        <p className="font-medium text-sm text-primary">Title tag formula:</p>
                        <p className="text-muted-foreground font-mono text-sm">[Service] in [City], [State] | [Company Name]</p>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-primary">Example:</p>
                        <p className="text-muted-foreground font-mono text-sm">Lawn Mowing in Austin, TX | Green Pro Lawn Care</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Title tag under 60 characters</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>H1 includes primary keyword + city</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>One H1 per page (not two or three)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Meta description under 160 characters with CTA</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* URL Structure */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                      URL Structure
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Clean, readable URLs help users and search engines understand what the page is about.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-destructive/10 rounded-lg p-4">
                        <p className="font-medium text-sm text-destructive mb-2">❌ Bad:</p>
                        <p className="text-muted-foreground font-mono text-xs break-all">yoursite.com/page?id=12345&ref=home</p>
                        <p className="text-muted-foreground font-mono text-xs break-all mt-1">yoursite.com/services-1</p>
                      </div>
                      <div className="bg-primary/10 rounded-lg p-4">
                        <p className="font-medium text-sm text-primary mb-2">✅ Good:</p>
                        <p className="text-muted-foreground font-mono text-xs">yoursite.com/lawn-mowing/</p>
                        <p className="text-muted-foreground font-mono text-xs mt-1">yoursite.com/service-areas/austin-tx/</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Use hyphens, not underscores</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Include keywords in URL slug</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Keep URLs short and descriptive</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Avoid random numbers/IDs</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Internal Linking */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</span>
                      Internal Linking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      Internal links help Google crawl your site and understand page relationships. They also keep visitors moving toward contact.
                    </p>
                    
                    <div className="bg-accent/30 rounded-lg p-4">
                      <p className="font-medium text-sm text-primary mb-2">Link flow example:</p>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="bg-background px-3 py-1 rounded-full border">Home</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border">Service Pages</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border">City Pages</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="bg-background px-3 py-1 rounded-full border">Contact</span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Checklist:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Every page links to at least 2–3 other pages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Use descriptive anchor text (not "click here")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Link related services to each other</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Every service/city page links to contact</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Content Depth */}
              <div className="mb-10">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">4</span>
                      Content Depth
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      "Thin content" = pages with very little useful information. Google prefers pages that actually answer questions.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-destructive/10 rounded-lg p-4">
                        <p className="font-medium text-sm text-destructive mb-2">❌ Thin page:</p>
                        <p className="text-muted-foreground text-sm">"We offer lawn mowing services. Call us for a quote."</p>
                        <p className="text-xs text-muted-foreground mt-2">(~10 words, no detail)</p>
                      </div>
                      <div className="bg-primary/10 rounded-lg p-4">
                        <p className="font-medium text-sm text-primary mb-2">✅ Strong page:</p>
                        <p className="text-muted-foreground text-sm">What's included, process, pricing info, FAQs, photos, CTAs, 400+ words.</p>
                        <p className="text-xs text-muted-foreground mt-2">(Answers real questions)</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Minimum content targets:</h4>
                      <ul className="space-y-1 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Service pages: 400–800+ words</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>City pages: 300–500+ words with local context</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>Homepage: 500–1000+ words across sections</span>
                        </li>
                      </ul>
                      <p className="text-sm text-muted-foreground mt-3">
                        Need help with what to write? See our <NavLink to="/lawn-care-website-content" className="text-primary hover:underline">lawn care website content guide</NavLink>.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Service Pages That Rank */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <FileText className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Service Pages That Rank
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Each service you offer needs its own page. Here's the structure that works.
              </p>

              <Card>
                <CardContent className="p-6">
                  <ul className="space-y-3 text-muted-foreground mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">H1:</strong> "[Service] in [City]" (e.g., "Lawn Mowing in Austin, TX")</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">What's included:</strong> Bullet list of deliverables</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Process:</strong> How it works in 3–5 steps</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">Pricing:</strong> "Starting at $X" or "Request a quote"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">FAQs:</strong> 3–5 questions specific to this service</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <span><strong className="text-foreground">CTA:</strong> Phone number + quote button above the fold and at bottom</span>
                    </li>
                  </ul>

                  <div className="text-center">
                    <NavLink to="/service-pages-that-rank">
                      <Button>
                        Read the Full Service Page Guide
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </NavLink>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Area Pages */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <MapPin className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Service Area Pages
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                City pages help you rank for "[service] in [city]" searches. But don't spam.
              </p>

              <Card>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">Best practices:</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Create individual pages for your top 5–10 cities</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Add unique content: mention neighborhoods, projects done there, local details</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Include photos from jobs in that area when possible</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span>Link to services and contact page</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-destructive/10 rounded-lg p-4">
                    <p className="font-medium text-sm text-destructive mb-2">⚠️ Don't do this:</p>
                    <p className="text-muted-foreground text-sm">
                      Creating 50 city pages that are identical except for the city name. Google sees this as spam and it can hurt your rankings.
                    </p>
                  </div>

                  <div className="text-center">
                    <NavLink to="/service-area-pages-local-seo">
                      <Button>
                        Read the Full Service Area Guide
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </NavLink>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* GBP Alignment */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Globe className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  GBP Alignment (Website + Google Business Profile)
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Your website and Google Business Profile should match perfectly. Inconsistencies confuse Google.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">NAP Consistency</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                      Name, Address, Phone must be identical on:
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Your website footer
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Contact page
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Google Business Profile
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        All citations (Yelp, etc.)
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Services Alignment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3">
                      Services listed on GBP should match your website:
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Same service names
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Each GBP service links to a website page
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-primary" />
                        Primary category matches main offering
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Review Strategy Basics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-sm mb-2">How to ask:</p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Text/email after job completion</li>
                        <li>• Include direct link to leave review</li>
                        <li>• Follow up once if no response</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-sm mb-2">Where to display:</p>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• Homepage (2–3 featured)</li>
                        <li>• Dedicated reviews page</li>
                        <li>• Relevant service pages</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <NavLink to="/growth-add-ons/gbp" className="text-primary hover:underline text-sm">
                      → Learn more about Google Business Profile optimization
                    </NavLink>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Technical SEO + Speed */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Zap className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Technical SEO + Speed
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Fast, mobile-friendly sites rank better. Here's what matters.
              </p>

              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Core Web Vitals (Plain English)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <div>
                          <p className="font-medium">LCP (Largest Contentful Paint)</p>
                          <p className="text-sm text-muted-foreground">How fast the main content loads. Target: under 2.5 seconds.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <div>
                          <p className="font-medium">INP (Interaction to Next Paint)</p>
                          <p className="text-sm text-muted-foreground">How fast the page responds to clicks. Target: under 200ms.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                        <div>
                          <p className="font-medium">CLS (Cumulative Layout Shift)</p>
                          <p className="text-sm text-muted-foreground">How much stuff moves around while loading. Target: under 0.1.</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Speed Checklist</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
                      {[
                        "Use WebP image format",
                        "Compress images under 150kb",
                        "Enable lazy loading for images",
                        "Minimize plugins/scripts",
                        "Use fast, reliable hosting",
                        "Enable browser caching",
                        "Minify CSS and JavaScript",
                        "Use a CDN for assets",
                        "Optimize web fonts",
                        "Test with PageSpeed Insights"
                      ].map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Mobile UX Must-Haves</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Sticky call button:</strong> Visible while scrolling so visitors can call anytime</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Tap targets:</strong> Buttons at least 44x44 pixels, not cramped together</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Form spacing:</strong> Form fields large enough to tap and fill on mobile</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Readable text:</strong> 16px minimum, no horizontal scrolling required</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Schema */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Code className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Schema Markup
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                Schema is code that helps Google understand your business better. It can improve how you appear in search results.
              </p>

              <Card>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3">What schema types to use:</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">LocalBusiness or Organization:</strong> On your homepage with business name, address, phone, hours</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">Service:</strong> On service pages to describe what you offer</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                        <span><strong className="text-foreground">FAQPage:</strong> On pages with FAQ sections to potentially get rich snippets</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-accent/30 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Note:</strong> Don't add fake reviews or ratings to schema. Google can penalize sites for deceptive schema markup. Only include aggregateRating if you have a system that collects verified reviews.
                    </p>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    All our <NavLink to="/lawn-care-website-design" className="text-primary hover:underline">done-for-you lawn care websites</NavLink> include proper schema markup.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
                Common Local SEO Mistakes (And How to Fix Them)
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
                Avoid these lawn care website SEO mistakes that hurt your local rankings.
              </p>

              <div className="grid gap-4">
                {commonMistakes.map((item, index) => (
                  <Card key={index}>
                    <CardContent className="p-4 flex flex-col md:flex-row gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Mistake:</p>
                          <p className="text-muted-foreground text-sm">{item.mistake}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 flex-1">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-sm">Fix:</p>
                          <p className="text-muted-foreground text-sm">{item.fix}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 30-Day Action Plan */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-4">
                <Calendar className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  30-Day Local SEO Action Plan
                </h2>
              </div>
              <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                A week-by-week plan to improve your lawn care website's local SEO.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      Week 1: Foundation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Fix title tags and H1s with service + city</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>List all services on website and GBP</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Ensure NAP is consistent everywhere</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add CTA above the fold on every page</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      Week 2: Service Pages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Create/improve 3–5 dedicated service pages</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add 400+ words per service page</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Include what's included, process, FAQs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add real photos of your work</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      Week 3: City Pages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Create 5–10 city/area pages</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Add unique content per city</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Include local project photos if possible</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Link city pages to services and contact</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">4</span>
                      Week 4: Polish + Reviews
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Verify GBP is fully aligned with website</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Set up review request process</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Check all internal links are working</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>Run PageSpeed test and fix major issues</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions
              </h2>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 bg-primary/5">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Want Us to Handle Your Website SEO?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We build lawn care websites with local SEO baked in—so you can focus on mowing lawns, not optimizing pages.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </a>
                <NavLink to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8">
                    Contact Us
                  </Button>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <CalendlyEmbed />

        {/* Related Reading */}
        <section className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 justify-center mb-8">
                <BookOpen className="h-6 w-6 text-primary" />
                <h2 className="text-2xl md:text-3xl font-bold text-center">
                  Related Reading
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Lawn Care Website Design", description: "See what's included in our done-for-you websites.", href: "/lawn-care-website-design" },
                  { title: "Lawn Care Website Cost", description: "Understand pricing options and what affects cost.", href: "/lawn-care-website-cost" },
                  { title: "Service Pages That Rank", description: "Full guide to structuring service pages for SEO.", href: "/service-pages-that-rank" },
                  { title: "Service Area Pages for Local SEO", description: "How to create city pages that rank.", href: "/service-area-pages-local-seo" },
                  { title: "Lawn Care Website Content Guide", description: "What to write on each page of your site.", href: "/lawn-care-website-content" },
                  { title: "Website Subscription Pricing", description: "Our month-to-month website packages.", href: "/websites" },
                  { title: "Google Business Profile Optimization", description: "Get more calls from local search.", href: "/growth-add-ons/gbp" },
                  { title: "Free Resources & Blog", description: "More guides for lawn care businesses.", href: "/resources" }
                ].map((item, index) => (
                  <NavLink key={index} to={item.href}>
                    <Card className="h-full hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </CardContent>
                    </Card>
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="py-8 border-t">
          <div className="container px-4 md:px-6">
            <p className="text-center text-sm text-muted-foreground">
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteSEO;
